import { Checkbox, Gap, SvgIcon } from '@salecycle/ui-component-library';
import { ExitIntentAreas, TriggersConfigMap, TriggerType } from 'sections/onsite-campaigns/types';
import { InactivityConfiguration } from './InactivityConfiguration';
import { ExitIntentConfiguration } from './ExitIntentConfiguration/ExitIntentConfiguration';
import { InfoTooltip } from 'sections/onsite-campaigns/pages/CampaignEditor/components/InfoTooltip';
import styled from 'styled-components';
import { PageLoadConfiguration } from './PageLoadConfiguration';
import { OnClickConfiguration } from './OnClickConfiguration';
import { OnClickConfig } from 'sections/onsite-campaigns/types/onClick';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { validateOnClickSelector } from '../utils/validateOnClickSelector';

export type TriggersProps = {
  title: string;
  description: string;
  triggers: TriggersConfigMap;
  onTriggerChanged: <T extends keyof TriggersConfigMap>(type: T, config: TriggersConfigMap[T]) => void;
  id?: string;
};

const TooltipWrapper = styled.div`
  position: relative;
  top: -4px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.designToken.spacing1};
  margin-bottom: ${({ theme }) => theme.designToken.spacing3};
  color: var(--color-neutral-900, #1d2226);
  font-family: Whitney;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
`;

const Title = styled.div`
  margin-left: ${({ theme }) => theme.designToken.spacing1};
`;

export function Triggers({ title, description, triggers, onTriggerChanged, id }: TriggersProps) {
  const { t } = useTranslation('campaigns');
  const [selectorError, setSelectorError] = useState('');

  const handleSelectorChange = (value: string) => {
    const error = validateOnClickSelector(value, t('onsite.campaignOnClickTrigger.validationError'));
    setSelectorError(error);
    onTriggerChanged(TriggerType.OnClick, {
      active: true,
      value: { selector: value, overrideFreqCaps: triggers[TriggerType.OnClick]?.value.overrideFreqCaps ?? true },
    });
  };

  const handleOverrideFreqCapsChange = (overrideFreqCaps: boolean) => {
    onTriggerChanged(TriggerType.OnClick, {
      active: true,
      value: {
        selector: triggers[TriggerType.OnClick]?.value?.selector || '',
        overrideFreqCaps,
      },
    });
  };

  // Validate initial selector value if it exists
  useEffect(() => {
    const currentSelector = triggers[TriggerType.OnClick]?.value?.selector || '';
    if (currentSelector && triggers[TriggerType.OnClick]?.active) {
      const error = validateOnClickSelector(currentSelector, t('onsite.campaignOnClickTrigger.validationError'));
      setSelectorError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const triggersConfigLookup = {
    [TriggerType.ExitIntent]: (
      <ExitIntentConfiguration
        value={triggers[TriggerType.ExitIntent]?.value || []}
        onChange={value => onTriggerChanged(TriggerType.ExitIntent, { active: true, value })}
      />
    ),
    [TriggerType.Inactivity]: (
      <InactivityConfiguration
        value={triggers[TriggerType.Inactivity]?.value || 0}
        onChange={value => onTriggerChanged(TriggerType.Inactivity, { active: true, value })}
      />
    ),
    [TriggerType.PageLoad]: <PageLoadConfiguration />,
    [TriggerType.OnClick]: (
      <OnClickConfiguration
        value={triggers[TriggerType.OnClick]?.value.selector || ''}
        onChange={handleSelectorChange}
        overrideFreqCaps={triggers[TriggerType.OnClick]?.value?.overrideFreqCaps ?? true}
        onOverrideFreqCapsChange={handleOverrideFreqCapsChange}
        validationState={selectorError ? 'error' : 'complete'}
        validationMessage={selectorError}
      />
    ),
  };

  return (
    <div data-testid={id || 'messageTrigger'}>
      <TitleContainer>
        <SvgIcon name="hand-pointer" size="24" />
        <Title>{title}</Title>
        <TooltipWrapper>
          <InfoTooltip content={description} />
        </TooltipWrapper>
      </TitleContainer>
      {Object.keys(triggers).map(x => {
        const trigger = x as keyof TriggersConfigMap;
        const isChecked = triggers[trigger]?.active || false;
        const label = trigger.toString();

        const isPageLoadSelected = triggers[TriggerType.PageLoad]?.active || false;
        const isCurrentTriggerPageLoad = trigger === TriggerType.PageLoad;
        const isDisabled = isPageLoadSelected && !isCurrentTriggerPageLoad;

        const onChange = (checked: boolean) => {
          if (checked !== true && checked !== false) {
            return;
          }

          if (trigger === TriggerType.Inactivity) {
            onTriggerChanged(trigger, {
              active: checked,
              value: triggers[trigger]?.value ? (triggers[trigger]?.value as number) : 0,
            });
          }

          if (trigger === TriggerType.ExitIntent) {
            onTriggerChanged(trigger, {
              active: checked,
              value: triggers[trigger]?.value ? (triggers[trigger]?.value as ExitIntentAreas[]) : [],
            });
          }

          if (trigger === TriggerType.PageLoad) {
            onTriggerChanged(trigger, {
              active: checked,
              value: checked,
            });
          }
          if (trigger === TriggerType.OnClick) {
            onTriggerChanged(trigger, {
              active: checked,
              value: triggers[trigger]?.value
                ? (triggers[trigger]?.value as OnClickConfig)
                : { selector: '', overrideFreqCaps: true },
            });
          }
        };

        return (
          <span key={trigger}>
            <Checkbox
              id={label.toLocaleLowerCase()}
              label={label}
              checked={isChecked}
              disabled={isDisabled}
              onChange={onChange}
            />
            {isChecked ? <Gap bottom={16} /> : <Gap bottom={8} />}
            {isChecked && <div>{triggersConfigLookup[trigger]}</div>}
          </span>
        );
      })}
    </div>
  );
}
